import { useEffect, useState } from "react";
import { Star, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffH = (Date.now() - then) / 36e5;
  if (diffH < 24) return "Today";
  if (diffH < 48) return "Yesterday";
  const days = Math.floor(diffH / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const StarRow = ({ rating, size = 16 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        style={{ width: size, height: size }}
        className={n <= rating ? "fill-[#FF4500] text-[#FF4500]" : "text-white/25"}
      />
    ))}
  </div>
);

export default function ProductReviews({ productId, productTitle }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: "", rating: 5, body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    publicApi
      .listProductReviews(productId)
      .then((data) => setReviews(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || form.body.trim().length < 4) {
      toast.error("Please add your name and a short message (4+ characters)");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitReview({ product_id: productId, ...form, author_name: form.author_name.trim(), body: form.body.trim() });
      setForm({ author_name: "", rating: 5, body: "" });
      setJustSubmitted(true);
      toast.success("Thanks! Your review will appear once Cam approves it.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit — try again in a moment";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <section data-testid="product-reviews" className="border-t border-white/10 mt-16 pt-12">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <p className="overline mb-2">What people are saying</p>
            <h2 className="font-display text-3xl md:text-4xl">Reviews {reviews.length > 0 && <span className="text-white/40 text-2xl">({reviews.length})</span>}</h2>
          </div>
          {avg && (
            <div className="text-right shrink-0">
              <div className="flex items-center gap-2 justify-end">
                <StarRow rating={Math.round(parseFloat(avg))} size={20} />
                <span className="font-display text-2xl text-[#FF4500]" data-testid="reviews-average">{avg}</span>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">Average rating</p>
            </div>
          )}
        </div>

        {/* Reviews list */}
        <div className="space-y-5 mb-12" data-testid="reviews-list">
          {loading ? (
            <p className="text-white/40 text-sm italic">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-white/15 bg-[#0F0F0F]">
              <p className="text-white/60">No reviews yet — be the first to share your thoughts on <span className="text-white">{productTitle}</span>.</p>
            </div>
          ) : (
            reviews.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="border border-white/10 bg-[#141414] p-5">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <p className="font-display tracking-wider text-white">{r.author_name}</p>
                    <StarRow rating={r.rating} />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 shrink-0">{timeAgo(r.created_at)}</p>
                </div>
                <p className="text-white/80 leading-relaxed whitespace-pre-line text-[15px] mt-3">{r.body}</p>
              </article>
            ))
          )}
        </div>

        {/* Submission form */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6" data-testid="review-form-wrap">
          <p className="overline mb-4">Write a review</p>
          {justSubmitted ? (
            <div data-testid="review-thank-you" className="flex items-start gap-3 text-white/80">
              <CheckCircle2 className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
              <div>
                <p className="font-display tracking-wider text-white text-lg">Thanks for the love</p>
                <p className="text-sm text-white/70 mt-1">Your review is in the queue and will appear here once Cam gives it the tick. Feel free to <button data-testid="review-add-another" className="text-[#FF4500] underline" onClick={() => setJustSubmitted(false)}>add another one</button>.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <input
                  data-testid="review-name-input"
                  type="text"
                  placeholder="Your name"
                  value={form.author_name}
                  onChange={(e) => setForm({ ...form, author_name: e.target.value.slice(0, 60) })}
                  maxLength={60}
                  className="bg-[#0A0A0A] border border-white/20 text-white px-3 py-2.5 focus:outline-none focus:border-[#FF4500]"
                  required
                />
                <div className="flex items-center gap-3 bg-[#0A0A0A] border border-white/20 px-3 py-2.5" data-testid="review-rating-picker">
                  <span className="text-[10px] uppercase tracking-widest text-white/50 mr-1">Rating</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onClick={() => setForm({ ...form, rating: n })}
                      className="focus:outline-none"
                      aria-label={`Give ${n} star${n > 1 ? "s" : ""}`}
                    >
                      <Star className={`w-5 h-5 transition-colors ${n <= form.rating ? "fill-[#FF4500] text-[#FF4500]" : "text-white/25 hover:text-white/60"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                data-testid="review-body-input"
                placeholder={`How did ${productTitle} land for you?`}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 800) })}
                rows={4}
                maxLength={800}
                className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-2.5 focus:outline-none focus:border-[#FF4500] resize-none"
                required
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] uppercase tracking-widest text-white/40">
                  Reviews are moderated · no email required · {form.body.length}/800
                </p>
                <button
                  data-testid="review-submit"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF4500] text-black font-display tracking-wider hover:bg-[#FF4500]/80 transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? "Sending…" : "Submit review"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
